/**
 * LLM-based classifier — fallback for files the heuristic regex returns 'other' on.
 *
 * Architecture:
 *   - Client batches a list of ambiguous files (id + name + folderPath)
 *   - Calls a Supabase edge function `classify-documents` with a structured
 *     prompt against an existing LLM provider (Mistral via the chat endpoint)
 *   - Returns a Map<fileId, DocumentType>
 *
 * Production wiring TODO: deploy `classify-documents` edge function. Until
 * deployed, the call no-ops and returns an empty map → caller keeps heuristic
 * classification (which is already 'other' for these files). No regression.
 */

import type { DataRoomDocument } from '@/lib/state/dataroom-store';
import { reportError } from '@/lib/errors';

export type DocumentType = DataRoomDocument['type'];

export interface AmbiguousFile {
  id: string;
  name: string;
  folderPath?: string;
}

export interface LLMClassificationResult {
  /** id → suggested type. Files the LLM couldn't confidently classify are absent. */
  classifications: Map<string, DocumentType>;
  /** True if the underlying endpoint is not deployed; caller can still proceed. */
  endpointMissing: boolean;
}

/** No-op classification result used as a stable fallback. */
const EMPTY: LLMClassificationResult = {
  classifications: new Map(),
  endpointMissing: false,
};

/**
 * Classify ambiguous files via the LLM endpoint.
 *
 * Returns an empty result (with `endpointMissing: true`) if the edge function
 * has not been deployed — the caller should treat this as a no-op and keep the
 * heuristic classification.
 */
export async function classifyAmbiguousByLLM(
  files: AmbiguousFile[],
): Promise<LLMClassificationResult> {
  if (files.length === 0) return EMPTY;

  try {
    // TODO(backend): deploy `classify-documents` edge function with this contract:
    //   POST /functions/v1/classify-documents
    //   body: { files: [{ id, name, folderPath? }] }
    //   response: { classifications: { [id]: 'pitch_deck'|'financials'|... } }
    //
    // Until deployed, dynamic-require the edge-functions module and invoke if
    // present; otherwise return EMPTY with endpointMissing=true.
    const edgeFunctions = await import('@/lib/supabase/edge-functions');
    type ClassifyFn = (
      payload: { files: AmbiguousFile[] },
    ) => Promise<{ data?: { classifications?: Record<string, DocumentType> } }>;
    const classifyDocuments = (edgeFunctions as unknown as { classifyDocuments?: ClassifyFn })
      .classifyDocuments;

    if (typeof classifyDocuments !== 'function') {
      return { classifications: new Map(), endpointMissing: true };
    }

    const response = await classifyDocuments({ files });
    const map = new Map<string, DocumentType>();
    const raw = response?.data?.classifications ?? {};
    for (const [id, type] of Object.entries(raw)) {
      map.set(id, type);
    }
    return { classifications: map, endpointMissing: false };
  } catch (err) {
    reportError(err, {
      context: 'classifyAmbiguousByLLM',
      level: 'warning',
      silent: true,
    });
    return EMPTY;
  }
}
